import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { LoginGlobalError, LoginGlobalInfo, URLParameters } from '../../../types';
import { EventAttributes } from '../../../utilities/analytics/analytics-web-client';
import { lookupApplication } from '../../../utilities/applications';
import { getEmailFromLoginHint, useLoginHint } from '../../../utilities/hooks/login-hint';
import { useLoginState } from '../../../utilities/hooks/useLoginState';
import { useRegisterComponent } from '../../orchestrator';

export type LoginRecoveryOptionsType =
  | {
      expectedEmail: string;
      recoveryToken: string;
    }
  | undefined;

export const useLoginRecoveryOptions = () => {
  const { state } = useLoginState();
  const { login } = state || {};
  const loginRecoveryOptions = useCallback(
    (email: string): LoginRecoveryOptionsType => {
      return login?.recoveryToken && email
        ? { expectedEmail: email, recoveryToken: login.recoveryToken }
        : undefined;
    },
    [login?.recoveryToken]
  );

  return loginRecoveryOptions;
};

export const useCobrandingApplication = () => {
  const { state } = useLoginState();
  const { cobranding } = state || {};

  const applicationData = useMemo(() => {
    if (cobranding === undefined) return null;
    return lookupApplication(cobranding);
  }, [cobranding]);

  return applicationData?.coreApplication;
};

export const useSearchParamCodes = () => {
  const location = useLocation();

  const queryParams: URLParameters = Object.fromEntries(
    new URLSearchParams(location.search).entries()
  );
  const infoCode = useMemo(
    () => (queryParams.infoCode ? ({ code: queryParams.infoCode } as LoginGlobalInfo) : null),
    [queryParams.infoCode]
  );

  const errorCode = useMemo(
    () => (queryParams.errorCode ? ({ code: queryParams.errorCode } as LoginGlobalError) : null),
    [queryParams.errorCode]
  );

  return {
    errorCode,
    infoCode,
  };
};

export const useEmailFromSearchParams = () => {
  useRegisterComponent(useEmailFromSearchParams.name);

  const loginHint = useLoginHint();
  const [queryParams] = useSearchParams();
  return getEmailFromLoginHint(loginHint) || queryParams.get('email') || '';
};

export const useAppendReasonViewingScreen = () => {
  const { search } = useLocation();

  const infoCode = useMemo(() => {
    const queryParams = new URLSearchParams(search);
    return queryParams.has('infoCode')
      ? ({ code: queryParams.get('infoCode') } as LoginGlobalInfo)
      : null;
  }, [search]);

  const appendReasonViewingScreen = useCallback(
    (eventAttributes: EventAttributes) => {
      if (infoCode) {
        switch (infoCode.code) {
          case 'migratingExistingUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'migratingUser',
            };
          case 'existingUserSignupAttempt':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'existingUserSignupAttempt',
            };
          case 'invitedUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'invitedUser',
            };
          default:
            return eventAttributes;
        }
      }
      return eventAttributes;
    },
    [infoCode]
  );

  return appendReasonViewingScreen;
};
